<template lang="pug">
	.creative--common-service-criteria-editor
		Popup(v-if="popupVisible", :popupVisible.sync="popupVisible", :width="640", :height="480", :title="$tCaption('Criteria editor')")
			.flex-row.full-width.space.align-center(:class="{ 'controls-disabled': isLoading }")
				DxFilterBuilder(v-if="initiated", ref="filterBuilder", :customOperations="[]", :fields="criteriaFields" :value.sync="criteria", :allow-hierarchical-fields="true", @value-changed="e => onFilterBuilderValueChanged(e)")
					template(v-slot:output-template="{ data }")
						SSMLVariativeTextEditorHandler(@created="showEditorPopup(data)")
				Loader(v-if="!initiated || isLoading", :text="$tCaption('Loading', '@actions')", :fullscreen="true")
			template(slot="actions-right")
				DxButton(:text="$tCaption('Close', '@actions')", @click="popupVisible = false")
				DxButton(:text="$tCaption('Save', '@actions')", @click="save", type="success")
			template(slot="actions-left")
				slot(name="actions-left")
				DxButton(:text="$tCaption('Clear', '@actions')", @click="clearCriteria", type="normal", icon="refresh")
		Popup(v-if="editorPopup.visible" :popupVisible="editorPopup.visible", :width="420", @hiding="applyEditingOutputValue()", :autosize="true")
			.flex-col.space-large.wrap
				.flex-col(v-if="editorPopup.output")
					.font-big.upper.grey {{$tCaption('Value')}}
					SSMLVariativeTextParamEditor(:param="editorPopup.output.param", :value.sync="editorPopup.value")
				.flex-col.space.parameters
					.flex-col.space-large
						StructureCriteria.structure-criteria-editor(:service="getService(editorPopup.data)", :hideCriteria="true", 
							:params="getServiceParams(editorPopup.data)", :paramsCaptions="getServiceParams(editorPopup.data, true)",
							@change="(criteria, params, paramsCaptions, propagatedEditableParams) => onCriteriaChange(editorPopup.data, criteria, params, paramsCaptions, propagatedEditableParams)",
							:allowTemplateFieldCreation="allowKeyEditing", :fullStructure="true", :associations="editingAssociations", @action="onCriteriaAction",
							@template-field-change="(param, templateFieldId) => onTemplateFieldAssociationChanged(templateFieldId, param)", :usedServices="usedServices")
			template(slot="actions-right")
				DxButton(:text="$tCaption('Close', '@actions')", @click="applyEditingOutputValue(false)")
				DxButton(:text="$tCaption('Save', '@actions')", @click="editorPopup.applyValue = true; applyEditingOutputValue()", type="success", :disabled="!editorPopup.value")
			UsedServicesContextMenu(v-show="false", :servicesTextSource="stringify(criteria)", :templateFields="templateFields", @menu-items-change="v => usedServices = v.all")
</template>

<script>
	import _ from 'lodash';
	import Popup from 'app-core/Popup.vue';
	import serviceHelper from 'view/webServiceCaller/service-helper';
	import DxFilterBuilder from 'devextreme-vue/filter-builder';
	import SSMLVariativeTextParamEditor from 'view/insertion/SSMLVariativeTextParamEditor.vue';
	import SSMLVariativeTextEditorHandler from 'view/insertion/SSMLVariativeTextEditorHandler.vue';
	import Loader from 'app-core/Loader.vue';
	import DxButton from 'devextreme-vue/button';
	import StructureCriteria from 'view/webServiceCaller/StructureCriteria.vue';
	import UsedServicesContextMenu from 'view/insertion//UsedServicesContextMenu.vue';

	export default {
		translationCategory: 'creative.CommonServiceCriteriaEditor',
		name: 'creative--common-service-criteria-editor',
		components: {
			Popup, DxFilterBuilder, SSMLVariativeTextParamEditor, SSMLVariativeTextEditorHandler, Loader, DxButton, StructureCriteria, UsedServicesContextMenu
		},
		props: ['templateFields', 'allowKeyEditing'],
		data() {
			return {
				popupVisible: false,
				editorPopup: {
					visible: false,
					data: null,
					output: null,
					value: null,
					initValue: null,
					v: null,
					applyValue: false,
					dataField: null
				},
				criteriaFields: [],
				criteria: null,
				params: {},
				paramsCaptions: {},
				templateFieldsAssociation: {},
				initiated: false,
				cachedInputValues: {},
				services: [],
				isLoading: 0,
				usedServices: [],
				opts: null
			};
		},
		computed: {
			editingAssociations() {
				return this.templateFieldsAssociation && this.templateFieldsAssociation[this.editorPopup?.dataField];
			}
		},
		watch: {
			criteria() {
				this.fixRootElementsIfSelected();
			}
		},
		mounted() {
			if (__DEBUG__)
				window.commonServiceCriteriaEditor = this;
		},
		destroyed() {
			delete this.$modelFixing;
		},
		methods: {
			clearCriteria() {
				this.$confirm(void 0, this.$tCaption('Do you want to clear all?')).then(() => this.criteria = null);
			},
			checkCriteria() {
				if (!this.$refs.filterBuilder.instance._isReady) {
					this.criteria = null;
				}
			},
			stringify(data) {
				return JSON.stringify(data);
			},
			showEditorPopup(data) {
				this.editorPopup.data = data;
				this.editorPopup.dataField = data?.field?.dataField;
				this.editorPopup.visible = false;
				this.editorPopup.value = data.value || '';
				this.editorPopup.output = this.getOutput(data);
				this.editorPopup.applyValue = false;
				setImmediate(() => {
					this.editorPopup.visible = true;
				});
			},
			getOutput(e) {
				let service = this.services.find(f => e.field.dataField.indexOf(`service_${f.Oid}`) > -1);
				let serviceOutput = service.Outputs?.find(f => e.field.dataField.indexOf(`output_${f.Name}`) > -1);

				let value = e?.value !== void 0 ? e.value : (this.cachedInputValues[serviceOutput?.Name] || serviceOutput?.Value);
				let param = {
					id: e.field.dataField,
					caption: '',
					param: serviceOutput
				};

				this.editorPopup.value = (this.editorPopup.initValue = e.value || '')
					.replace(/\[editingOutputValue\]/g, '');

				e.setValue('[editingOutputValue]');

				Object.defineProperty(param, 'value', {
					get: () => value,
					set: (v) => {
						value = v;
						if (__DEBUG__)
							console.debug('Set value', { v, e });
						e.setValue(v);
						this.cachedInputValues[serviceOutput.Name] = v;
					}
				});

				if (serviceOutput?.DataType === 'boolean' && value === null) {
					setTimeout(() => {
						param.value = false;
					}, 200);
				}

				if (__DEBUG__)
					console.debug('getOutputs', { param, e });

				return param;
			},
			loadCriteriaFields() {
				if (this.initiated) return;
				this.isLoading++;
				return serviceHelper.getCriteriaFields().then(r => {
					this.criteriaFields = r.criteriaFields;
					this.services = r.services;
				}).finally(() => {
					this.isLoading--;
					this.initiated = true;
				});
			},
			onFilterBuilderValueChanged(e) {
				if (__DEBUG__)
					console.debug('onFilterBuilderValueChanged', e);

				this.removeUnusedVaraibles();
			},
			removeUnusedVaraibles() {
				const json = JSON.stringify(this.criteria);
				Object.keys(this.params).map(f => {
					if (json.indexOf(f) === -1) {
						delete this.params[f];
						delete this.paramsCaptions[f];
					}
				})
			},
			fixRootElementsIfSelected() {
				if (this.$modelFixing) return;
				this.$modelFixing = true;

				try {
					this.$refs.filterBuilder.instance._model = serviceHelper.fixRootElements(this.$refs.filterBuilder.instance._model, this.criteriaFields);
					this.criteria = serviceHelper.fixRootElements(this.criteria, this.criteriaFields);
				} finally {
					this.$nextTick(() => {
						this.$modelFixing = false;
						this.$refs.filterBuilder.instance._refresh();
					});
				}
			},
			applyEditingOutputValue() {
				const apply = this.editorPopup.applyValue;
				const value = `${(apply ? this.editorPopup.value : this.editorPopup.initValue) || ''}`
					.replace(/\[editingOutputValue\]/g, '');

				const criteria = [...(this.$refs.filterBuilder?.instance?._model || this.criteria || [])];

				const textCriteria = JSON.stringify(criteria)
					.replace(/\[editingOutputValue\]/g, value);
				if (__DEBUG__)
					console.debug('applyEditingOutputValue', { value, textCriteria, oldCriteria: JSON.stringify(criteria) });
				this.criteria = JSON.parse(textCriteria);
				this.editorPopup.visible = false;

				if (apply) {
					this.$emit('apply', this.editorPopup);
				}
			},
			getService(e) {
				return this.services.find(f => e.field.dataField.indexOf(`service_${f.Oid}`) > -1)?.instance;
			},
			getServiceParams(e, getCaptions) {
				const items = (getCaptions ? this.paramsCaptions : this.params) || [];
				const key = e.field.dataField;
				return items[key];
			},
			onCriteriaChange(e, criteria, params, paramsCaptions, propagatedEditableParams) {
				if (__DEBUG__)
					console.debug('On criteria change', { e, criteria, params, paramsCaptions, propagatedEditableParams });
				const allowedParams = Object.keys(params).filter(f => propagatedEditableParams && propagatedEditableParams[f]).reduce((res, item) => {
					res[item] = params[item];
					return res;
				}, {});
				let inputParamId = e.field.dataField;
				this.$set(this.params, inputParamId, allowedParams);
				if (!this.paramsCaptions) {
					this.paramsCaptions = {};
				}
				if (paramsCaptions && Object.keys(paramsCaptions)) {
					this.$set(this.paramsCaptions, inputParamId, paramsCaptions);
				}
			},
			onCriteriaAction(action, param) {
				switch(action) {
					case 'create-template-field': {
						if (__DEBUG__)
							console.debug('Create template field from param', { param });
						const templateField = serviceHelper.getTemplateFieldFromParam(param);
						this.$emit('create-template-field', templateField);
						break;
					}
				}
			},
			onTemplateFieldAssociationChanged(value, param) {
				if (!this.templateFieldsAssociation[this.editorPopup.data.field.dataField]) {
					this.$set(this.templateFieldsAssociation, this.editorPopup.data.field.dataField, {});
				}

				this.$set(this.templateFieldsAssociation[this.editorPopup.data.field.dataField], param.Name, value);
			},
			save() {
				if (__DEBUG__)
					console.debug('Save');

				const value = !this.criteria ? null : _.cloneDeep({
					criteria: this.criteria,
					params: this.params,
					paramsCaptions: this.paramsCaptions,
					templateFieldsAssociation: this.templateFieldsAssociation
				});
				
				this.$emit('save', value);
				this.popupVisible = false;
			},
			show(opts) {
				if (__DEBUG__)
					window.commonServiceCriteriaEditor = this;
				this.popupVisible = false;
				this.opts = opts;
				this.editorPopup.applyValue = false;
				this.loadCondition(opts);
				this.loadCriteriaFields();
				setImmediate(() => {
					if (this._isDestroyed) return;
					this.popupVisible = true;
					this.$nextTick(() => this.checkCriteria());
				});
			},
			loadCondition(opts) {
				this.criteria = _.cloneDeep(opts?.criteria) || null;
				this.params = _.cloneDeep(opts?.params) || {};
				this.paramsCaptions = _.cloneDeep(opts?.paramsCaptions) || {};
				this.templateFieldsAssociation = _.cloneDeep(opts?.templateFieldsAssociation) || {};
			}
		}
	};
</script>
<style lang='scss'>
	.creative--common-service-criteria-editor {
	}
</style>
